import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Container, Grid, Paper, Typography, Box, Button, Chip,
  CircularProgress, Alert, IconButton, Divider, Tooltip,
  Dialog, DialogContent, DialogTitle, TextField, MenuItem,
  Select, FormControl, InputLabel, FormControlLabel, Checkbox,
  Snackbar, Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  Photo as PhotoIcon,
  LocalOffer as TagIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DeleteOutline as DeleteIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Share as ShareIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { pagesAPI, photosAPI, authAPI } from '../services/api';
import { sortContributorsBySequence } from '../utils/contributorSort';
import { colors } from '../theme';

// ── helpers ──────────────────────────────────────────────────────────────────
// Strip Python 'None' string and blank values
const clean = (v) => (!v || v === 'None' ? '' : v);

const keywordRefNames = (refs) =>
  (refs || []).map((k) => (typeof k === 'string' ? k : k?.name)).filter(Boolean);

const keywordRefsLabel = (refs) => keywordRefNames(refs).join(', ');

const TagRow = ({ label, value }) => {
  const v = clean(value);
  if (!v) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>{v}</Typography>
    </Box>
  );
};

const TagChips = ({ label, value }) => {
  const v = clean(value);
  if (!v) return null;
  const items = v.split(',').map((s) => s.trim()).filter((s) => s && s !== 'None');
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {items.map((item) => (
          <Chip key={item} label={item} size="small" variant="outlined" />
        ))}
      </Box>
    </Box>
  );
};

const PageDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const { t, i18n } = useTranslation();

  const [imageZoomOpen, setImageZoomOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Admin edit state
  const isAdmin = authAPI.isAdmin();
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [saveError, setSaveError] = useState('');
  const [snackOpen, setSnackOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: page?.issue_title || '', url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    }
  };

  const { data: pageData, isLoading, error } = useQuery(
    ['page', id],
    () => pagesAPI.getById(id),
    { enabled: !!id }
  );

  const { data: clubsData } = useQuery('clubs', () => pagesAPI.getClubs(), { staleTime: 5 * 60 * 1000 });
  const { data: serviceTypesData } = useQuery('serviceTypes', () => pagesAPI.getServiceTypes(), { staleTime: 5 * 60 * 1000 });
  const { data: keywordsData } = useQuery('keywords', () => pagesAPI.getKeywords(), { staleTime: 5 * 60 * 1000 });

  const clubOptions = useMemo(
    () => sortContributorsBySequence(
      (clubsData?.data?.results || []).filter((c) => c && c !== 'None'),
    ),
    [clubsData],
  );

  const clubLookup = useMemo(() => {
    const map = {};
    clubOptions.forEach((opt) => {
      if (opt?.name_zh) map[opt.name_zh] = opt;
    });
    return map;
  }, [clubOptions]);

  const formatClubLabel = useCallback((nameZh) => {
    const club = clubLookup[nameZh];
    if (!club) return nameZh;
    const name = i18n.language === 'zh' ? club.name_zh : (club.name_en || club.name_zh);
    return `(${club.short_form}) ${name}`;
  }, [clubLookup, i18n.language]);

  const page = pageData?.data;

  // ── mutations ──────────────────────────────────────────────────────────────
  const saveMutation = useMutation(
    (data) => pagesAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['page', id]);
        setEditMode(false);
        setSnackOpen(true);
        setSaveError('');
      },
      onError: (err) => {
        setSaveError(err.response?.data ? JSON.stringify(err.response.data) : '儲存失敗，請重試。');
      },
    }
  );

  const deletePhotoMutation = useMutation(
    (photoId) => photosAPI.delete(photoId),
    {
      onSuccess: () => queryClient.invalidateQueries(['page', id]),
      onError: () => setSaveError('刪除圖片失敗，請重試。'),
    }
  );

  const uploadPhotoMutation = useMutation(
    (file) => photosAPI.uploadForPage(page.id, file),
    {
      onSuccess: () => queryClient.invalidateQueries(['page', id]),
      onError: () => setSaveError('上傳圖片失敗，請重試。'),
    }
  );

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleEnterEdit = () => {
    setEditFields({
      club_name: clean(page.club_name),
      service_type: clean(page.service_type),
      event_name: clean(page.event_name),
      event_date: clean(page.event_date),
      event_location: clean(page.event_location),
      beneficiary: clean(page.beneficiary),
      beneficiary_organization: clean(page.beneficiary_organization),
      co_organizer: clean(page.co_organizer),
      keyword_refs: page.keyword_refs || [],
      summary: clean(page.summary),
      is_excellent: !!page.is_excellent,
      is_content: !!page.is_content,
      is_searchable: !!page.is_searchable,
      page_type: page.page_type || 'content',
    });
    setSaveError('');
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSaveError('');
  };

  const handleSave = () => {
    const { keyword_refs: keywordRefs, ...rest } = editFields;
    saveMutation.mutate({
      ...rest,
      keyword_refs: (keywordRefs || []).map((k) => {
        if (typeof k === 'object' && k?.id) return k.id;
        if (typeof k === 'string') return k;
        return k;
      }),
    });
  };

  const handleField = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEditFields((prev) => ({ ...prev, [field]: val }));
  };

  const handleDownloadPage = () => {
    if (!page?.image) return;
    const link = document.createElement('a');
    link.href = page.image;
    link.download = `page_${page.issue_year}_${page.issue_quarter}_p${page.page_number}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPhoto = (photo) => {
    const link = document.createElement('a');
    link.href = photo.image;
    link.download = `photo_page${page.page_number}_${photo.photo_order}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadPhotoMutation.mutate(file);
    e.target.value = '';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">載入頁面時發生錯誤 / Error loading page</Alert>
      </Container>
    );
  }

  if (!page) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">找不到此頁面 / Page not found</Alert>
      </Container>
    );
  }

  const hasTaggings = !!(
    clean(page.club_name) || clean(page.service_type) || clean(page.event_name) || clean(page.event_date) ||
    clean(page.event_location) || clean(page.beneficiary) || clean(page.co_organizer) ||
    keywordRefsLabel(page.keyword_refs) || clean(page.summary)
  );

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Back nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'white', boxShadow: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary">返回</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={shareCopied ? '已複製連結!' : '分享這頁'}>
            <Button
              variant="outlined"
              size="small"
              startIcon={shareCopied ? <CheckIcon /> : <ShareIcon />}
              onClick={handleShare}
              sx={{
                borderColor: shareCopied ? 'success.main' : colors.royalBlue,
                color: shareCopied ? 'success.main' : colors.royalBlue,
                transition: 'all 0.2s',
              }}
            >
              {shareCopied ? '已複製' : '分享'}
            </Button>
          </Tooltip>
        </Box>

        <Grid container spacing={3} alignItems="flex-start">

          {/* LEFT: issue name + full page image */}
          <Grid item xs={12} md={5} lg={4}>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', position: 'sticky', top: 80 }}>
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${colors.royalBlue} 0%, #1565C0 100%)`,
                  color: 'white',
                  p: 2.5,
                }}
              >
                <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                  {page.issue_year} · {page.issue_quarter}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}>
                  {page.issue_title || `${page.issue_year} ${page.issue_quarter}`}
                </Typography>
              </Box>

              <Box
                sx={{
                  backgroundColor: '#1a1a1a',
                  cursor: 'zoom-in',
                  position: 'relative',
                  '&:hover .zoom-hint': { opacity: 1 },
                }}
                onClick={() => setImageZoomOpen(true)}
              >
                {page.image ? (
                  <>
                    <img
                      src={page.image}
                      alt={page.issue_title || ''}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                    <Box
                      className="zoom-hint"
                      sx={{
                        position: 'absolute', inset: 0,
                        bgcolor: 'rgba(0,0,0,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: 0, transition: 'opacity 0.2s',
                      }}
                    >
                      <ZoomInIcon sx={{ fontSize: 52, color: 'white' }} />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="grey.500">No image available</Typography>
                  </Box>
                )}
              </Box>


            </Paper>
          </Grid>

          {/* RIGHT: AI taggings (top) + photos (bottom) */}
          <Grid item xs={12} md={7} lg={8}>

            {/* AI Taggings */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <TagIcon sx={{ mr: 1, color: colors.royalBlue }} />
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>AI 標記資訊</Typography>
                {isAdmin && !editMode && (
                  <Tooltip title="編輯標記 (Admin)">
                    <IconButton size="small" onClick={handleEnterEdit}
                      sx={{ color: colors.royalBlue, border: `1px solid ${colors.royalBlue}`, borderRadius: 1 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mb: 2.5 }} />

              {editMode ? (
                /* ── Admin Edit Form ── */
                <Box>
                  {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={clubOptions}
                        value={(editFields.club_name || '').split(',').map((s) => s.trim()).filter((s) => s && s !== 'None')}
                        getOptionLabel={(opt) => {
                          if (typeof opt === 'string') return formatClubLabel(opt);
                          const name = i18n.language === 'zh' ? opt.name_zh : (opt.name_en || opt.name_zh);
                          return `(${opt.short_form}) ${name}`;
                        }}
                        isOptionEqualToValue={(opt, val) => {
                          const valStr = typeof val === 'string' ? val : val.name_zh;
                          return opt.name_zh === valStr;
                        }}
                        onChange={(_, v) => {
                          const names = v.map((x) => (typeof x === 'string' ? x : x.name_zh));
                          setEditFields((prev) => ({ ...prev, club_name: names.join(',') }));
                        }}
                        freeSolo
                        filterSelectedOptions
                        renderTags={(val, getTagProps) =>
                          val.map((opt, idx) => {
                            const key = typeof opt === 'string' ? opt : opt.name_zh;
                            return (
                              <Chip
                                label={formatClubLabel(key)}
                                size="small"
                                {...getTagProps({ index: idx })}
                                key={key}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth label={t('clubLabel')} />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={(serviceTypesData?.data?.results || []).filter(Boolean)}
                        value={(editFields.service_type || '').split(',').map((s) => s.trim()).filter((s) => s && s !== 'None')}
                        onChange={(_, v) => setEditFields((prev) => ({ ...prev, service_type: v.join(',') }))}
                        freeSolo
                        filterSelectedOptions
                        renderTags={(val, getTagProps) =>
                          val.map((opt, idx) => (
                            <Chip label={opt} size="small" {...getTagProps({ index: idx })} key={opt} />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth label="服務種類 / Service Type" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="活動名稱 / Event Name"
                        value={editFields.event_name} onChange={handleField('event_name')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="活動日期 / Event Date"
                        value={editFields.event_date} onChange={handleField('event_date')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="活動地點 / Location"
                        value={editFields.event_location} onChange={handleField('event_location')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="受惠者 / Beneficiary"
                        value={editFields.beneficiary} onChange={handleField('beneficiary')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="合作機構 / Co-organizer"
                        value={editFields.co_organizer} onChange={handleField('co_organizer')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={(keywordsData?.data?.results || []).filter(Boolean)}
                        value={editFields.keyword_refs || []}
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
                        isOptionEqualToValue={(opt, val) => {
                          const valId = typeof val === 'object' ? val.id : val;
                          return opt.id === valId;
                        }}
                        onChange={(_, v) => setEditFields((prev) => ({ ...prev, keyword_refs: v }))}
                        freeSolo
                        filterSelectedOptions
                        renderTags={(val, getTagProps) =>
                          val.map((opt, idx) => {
                            const label = typeof opt === 'string' ? opt : opt.name;
                            const key = typeof opt === 'object' ? opt.id : opt;
                            return (
                              <Chip label={label} size="small" {...getTagProps({ index: idx })} key={key} />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth label="關鍵字 / Keywords" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="摘要 / Summary" multiline rows={3}
                        value={editFields.summary} onChange={handleField('summary')} />
                    </Grid>

                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControlLabel control={
                        <Checkbox checked={editFields.is_excellent} onChange={handleField('is_excellent')} />
                      } label="優質投稿 (Excellent)" />
                      <FormControlLabel control={
                        <Checkbox checked={editFields.is_content} onChange={handleField('is_content')} />
                      } label="內容頁 (Content)" />
                      <FormControlLabel control={
                        <Checkbox checked={editFields.is_searchable} onChange={handleField('is_searchable')} />
                      } label="可搜尋 (Searchable)" />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 1.5, mt: 3, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelEdit}
                      disabled={saveMutation.isLoading}>
                      取消
                    </Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}
                      disabled={saveMutation.isLoading}
                      sx={{ bgcolor: colors.royalBlue }}>
                      {saveMutation.isLoading ? '儲存中...' : '儲存'}
                    </Button>
                  </Box>
                </Box>
              ) : (
                /* ── Read-only display ── */
                hasTaggings ? (
                  <Grid container columnSpacing={3}>
                    {clean(page.club_name) && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {t('clubLabel')}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {clean(page.club_name).split(',').map((s) => s.trim()).filter((s) => s && s !== 'None').map((s) => (
                              <Chip key={s} label={s} color="primary" size="small" />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {clean(page.service_type) && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            服務種類 / Service Type
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {clean(page.service_type).split(',').map((s) => s.trim()).filter((s) => s && s !== 'None').map((s) => (
                              <Chip key={s} label={s} color="secondary" size="small" />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {(clean(page.club_name) || clean(page.service_type)) && (
                      <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                    )}
                    <Grid item xs={12} sm={6}><TagRow label="活動名稱 / Event" value={page.event_name} /></Grid>
                    <Grid item xs={12} sm={6}><TagRow label="活動日期 / Date" value={page.event_date} /></Grid>
                    <Grid item xs={12} sm={6}><TagRow label="活動地點 / Location" value={page.event_location} /></Grid>
                    <Grid item xs={12} sm={6}><TagRow label="受惠者 / Beneficiary" value={page.beneficiary} /></Grid>
                    <Grid item xs={12}><TagRow label="合作機構 / Co-organizer" value={page.co_organizer} /></Grid>
                    {keywordRefNames(page.keyword_refs).length > 0 && (
                      <Grid item xs={12}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            關鍵字 / Keywords
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {keywordRefNames(page.keyword_refs).map((item) => (
                              <Chip key={item} label={item} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {clean(page.summary) && (
                      <Grid item xs={12}>
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            摘要 / Summary
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.7 }}>{clean(page.summary)}</Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">此頁暫無 AI 標記資訊。</Typography>
                )
              )}
            </Paper>

            {/* Photos */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <PhotoIcon sx={{ mr: 1, color: colors.royalBlue }} />
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                  頁面圖片{page.photos?.length ? ` (${page.photos.length})` : ''}
                </Typography>
                {isAdmin && editMode && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handlePhotoUpload}
                    />
                    <Tooltip title="上傳新圖片 (Admin)">
                      <Button size="small" variant="outlined" startIcon={<AddPhotoIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadPhotoMutation.isLoading}
                        sx={{ borderColor: colors.royalBlue, color: colors.royalBlue }}>
                        {uploadPhotoMutation.isLoading ? '上傳中...' : '新增圖片'}
                      </Button>
                    </Tooltip>
                  </>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {page.photos && page.photos.length > 0 ? (
                <Grid container spacing={1.5}>
                  {page.photos.map((photo) => (
                    <Grid item xs={6} sm={4} md={3} key={photo.id}>
                      <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                        <Box
                          onClick={() => !editMode && setSelectedPhoto(photo)}
                          sx={{
                            cursor: editMode ? 'default' : 'pointer',
                            '&:hover img': !editMode ? { opacity: 0.82, transform: 'scale(1.04)' } : {},
                          }}
                        >
                          <img
                            src={photo.thumbnail || photo.image}
                            alt={`Photo ${photo.photo_order}`}
                            loading="lazy"
                            style={{ width: '100%', height: 'auto', display: 'block', transition: 'opacity 0.2s, transform 0.25s' }}
                          />
                        </Box>
                        {isAdmin && editMode && (
                          <Tooltip title="刪除此圖片">
                            <IconButton
                              size="small"
                              onClick={() => deletePhotoMutation.mutate(photo.id)}
                              sx={{
                                position: 'absolute', top: 4, right: 4,
                                bgcolor: 'rgba(220,38,38,0.85)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(220,38,38,1)' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">此頁暫無圖片。</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Full-page zoom */}
      <Dialog open={imageZoomOpen} onClose={() => setImageZoomOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {page.issue_title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="下載頁面">
              <span>
                <IconButton onClick={handleDownloadPage} disabled={!page.image} size="small">
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={() => setImageZoomOpen(false)} size="small"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1, backgroundColor: '#111' }}>
          <img src={page.image} alt={page.issue_title || ''} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </DialogContent>
      </Dialog>

      {/* Photo lightbox */}
      <Dialog open={!!selectedPhoto} onClose={() => setSelectedPhoto(null)} maxWidth="lg" fullWidth>
        {selectedPhoto && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
              <Typography variant="subtitle1">圖片 #{selectedPhoto.photo_order}</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton onClick={() => setSelectedPhoto(null)}><CloseIcon /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 1 }}>
              <img src={selectedPhoto.image} alt={`Photo ${selectedPhoto.photo_order}`}
                style={{ width: '100%', height: 'auto', borderRadius: 8 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, px: 1 }}>
                {page.issue_title}{clean(page.club_name) ? ` · ${clean(page.club_name)}` : ''}
              </Typography>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Save success snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message="儲存成功 / Saved successfully"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PageDetailPage;